<script setup>
import { computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import UserBar from '@/js/core/components/parts/user-bar/UserBar.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import PageHeading from '@/js/core/components/parts/PageHeading.vue';
import TrayTypeFilter from '@/js/core/components/parts/trays/TrayTypeFilter.vue';
import TraysGrid from '@/js/core/components/parts/trays/TraysGrid.vue';
import { useTraysListStore } from '@/js/core/stores/trays-list-store';
import { useCurrentPageStore } from '@/js/core/stores/current-page-store';
import { useUserStore } from '@/js/core/stores/user-store';
import { FILTER_OPTIONS } from '@/js/core/constants/tray-constants';

const router = useRouter();
const route = useRoute();
const traysStore = useTraysListStore();
const currentPageStore = useCurrentPageStore();
const userStore = useUserStore();

const trays = computed(() => traysStore.trays);
const isLoading = computed(() => traysStore.isLoading);
const errorMessage = computed(() => traysStore.error);
const hasMore = computed(() => traysStore.hasMore);
const selectedTrayType = computed({
  get: () => traysStore.filter,
  set: value => {
    const hadAll = traysStore.filter.length === 0;
    const clickedAll = value.includes('') && hadAll === false;

    if (clickedAll || value.length === 0) {
      // User clicked "All" or cleared all filters
      traysStore.setFilter([]);
    } else {
      // User clicked a specific filter — strip "All" if present
      traysStore.setFilter(value.filter(v => v !== ''));
    }
  },
});

const trayTypeOptions = FILTER_OPTIONS;

const handleStartNewTray = () => {
  router.push({ name: 'trays-new' });
};

onMounted(() => {
  traysStore.fetchTrays();
});

// Refetch Trays on navigation from Tray modal to Trays index
watch(
  () => route.name,
  name => {
    if (name === 'trays') traysStore.fetchTrays();
  }
);
</script>

<template>
  <UserBar />
  <NavigationPanel />
  <main id="main-content" class="trays-page-main-content">
    <section id="page-content">
      <header class="trays-page-header">
        <PageHeading
          :title="currentPageStore.title || 'Trays'"
          :description="currentPageStore.infoContent"
        >
          <template #actions>
            <button
              v-if="!userStore.isGuest"
              class="btn-primary"
              type="button"
              @click="handleStartNewTray"
            >
              Start New Tray
            </button>
          </template>
        </PageHeading>
      </header>

      <div v-if="!userStore.isGuest" class="trays-page-filters">
        <TrayTypeFilter v-model="selectedTrayType" :options="trayTypeOptions" />
      </div>

      <div v-if="isLoading && trays.length === 0" class="trays-page-loading">Loading trays...</div>

      <div v-else-if="errorMessage && trays.length === 0" class="trays-page-error" role="alert">
        {{ errorMessage }}
      </div>

      <div v-else-if="trays.length === 0" class="trays-page-empty">
        No trays found. Start by creating a new tray.
      </div>

      <template v-else>
        <TraysGrid :trays="trays" />

        <p v-if="isLoading" class="trays-page-loading-more" role="status">Loading…</p>

        <div v-else-if="errorMessage" class="trays-page-load-more-error" role="alert">
          <p class="trays-page-load-more-error__message">{{ errorMessage }}</p>
          <button
            type="button"
            class="trays-page-load-more-error__retry"
            @click="traysStore.loadMore()"
          >
            Try again
          </button>
        </div>

        <button
          v-else-if="hasMore"
          type="button"
          class="trays-page-load-more"
          @click="traysStore.loadMore()"
        >
          View more
        </button>
      </template>
    </section>
  </main>
</template>
